import { Avatar, Box, Fade, SxProps, Tooltip } from '@mui/material';

import { useTheme } from '@mui/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// helpers

// types
import { Theme } from '@mui/system';
import { MerchantType } from 'types/components/shared';
import { useStringAvatarProps } from './useStringAvatarProps';
import { hexIsLight } from 'utils/helpers';

export interface MerchantChipType extends MerchantType {
    isSelected: boolean;
}

export const avatarCommonProps = {
    border: '2px solid #fff',
    transitions: 'all 420ms linear'
    // width: 40,
    // height: 40,
    // fontSize: 16
};

export const MerchantAvatar = ({
    avatarStyles,
    containerStyles,
    feedback = true,
    handleClick = () => {},
    merchant,
    readOnly = false,
    size,
    disabled
}: {
    avatarStyles?: SxProps<Theme>;
    containerStyles?: SxProps<Theme>;
    feedback?: boolean;
    handleClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
    merchant: MerchantChipType;
    readOnly?: boolean;
    disabled?: boolean;
    size: 'small' | 'medium' | 'large';
}) => {
    const theme = useTheme();

    const { sx: styles, children } = useStringAvatarProps({ merchant, size });

    const bColor = hexIsLight(styles.bgcolor) ? '#008724' : theme.palette.success.main;

    const borderColor = merchant.isSelected && feedback ? bColor : 'transparent';

    const font = {
        small: 13,
        medium: 14,
        large: 17
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                cursor: readOnly ? 'initial' : 'pointer',
                position: 'relative',
                zIndex: 2,
                ...containerStyles
            }}
        >
            <Fade in={merchant.isSelected && feedback}>
                <Box
                    component={CheckCircleIcon}
                    sx={{
                        position: 'absolute',
                        right: 2,
                        bottom: 0,
                        fontSize: font[size],
                        color: bColor,
                        zIndex: 1
                    }}
                />
            </Fade>
            <Tooltip title={disabled || readOnly ? '' : merchant.name}>
                <Avatar
                    sx={{
                        ...styles,
                        background: disabled ? '#4b5763' : styles.bgcolor,
                        color: disabled ? theme.palette.grey[200] : styles.color,
                        borderWidth: 3,
                        borderColor,
                        transition: 'all 420ms linear',
                        cursor: readOnly || disabled ? 'initial' : 'pointer',
                        fontSize: font[size],
                        opacity: readOnly ? (disabled ? 1 : 0.7) : 1,
                        fontWeight: 500,
                        ...avatarStyles
                    }}
                >
                    {children}
                </Avatar>
            </Tooltip>
        </Box>
    );
};

MerchantAvatar.defaultProps = {
    // eslint-disable-next-line react/default-props-match-prop-types
    size: 'medium'
};

export const ShowMoreButton = ({
    moreText,
    handleClick,
    size,
    isSelected
}: {
    size: any;
    moreText: String;
    handleClick: (e: any) => void;
    isSelected?: boolean;
}) => {
    const theme = useTheme();

    const borderColor = isSelected ? theme.palette.secondary.main : 'transparent';

    return (
        <Box onClick={handleClick} sx={{ position: 'relative' }}>
            <Fade in={isSelected}>
                <Box
                    component={CheckCircleIcon}
                    sx={{
                        position: 'absolute',
                        right: 2,
                        bottom: 0,
                        fontSize: 14,
                        color: borderColor,
                        zIndex: 1
                    }}
                />
            </Fade>
            <Avatar
                sx={{
                    ...avatarCommonProps,
                    ...getSizeProps(size),
                    bgcolor: '#d3d3d3',
                    color: theme.palette.getContrastText('#d3d3d3'),
                    borderColor: borderColor,
                    borderWidth: 3,
                    mr: 0,
                    cursor: 'pointer'
                }}
            >
                {moreText}+
            </Avatar>
        </Box>
    );
};

export const customScrollBar = {
    '&::-webkit-scrollbar': {
        width: '5px'
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#c2c2c2',
        borderRadius: '5px'
    }
};

export const getSizeProps = (size: 'small' | 'medium' | 'large') => {
    const sizes = {
        small: 28,
        medium: 30,
        large: 35
    };

    const font = {
        small: 13,
        medium: 15,
        large: 16
    };

    return { width: sizes[size], height: sizes[size], fontSize: font[size] };
};
